import React, {useState, useEffect, useRef} from 'react';

import {
    CircularProgress,
    Paper,
    Typography
} from "@material-ui/core";
import {Alert} from "@material-ui/lab";
import useStyles from "../styles/styles";
import {useQuery, useMutation} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import TrafficSourceStateGroupTable from "../components/TrafficSourceStateGroup/TrafficSourceStateGroupTable";
import StateModal from "../components/TrafficSourceStateGroup/StateModal";
import TrafficSourceIdListModal from "../components/TrafficSourceStateGroup/TrafficSourceListModal";
import _ from 'lodash';

const TrafficSourceStateGroups = () => {
    const classes = useStyles();
    const [success, setSuccess] = useState(false);
    const [stateList, setStateList] = useState('');
    const [trafficSourceIdList, setTrafficSourceIdList] = useState('');
    const [rowData, setRowData] = useState(false);
    const [modalData, setModalData] = useState({
        modalType: '',
        data: false,
        showAddRow: false,
        value: '',
        category: ''
    });
    const resetFinalValue = {
        stateList: [],
        trafficSourceIdList: []
    };
    const [finalValue, setFinalValue] = useState(resetFinalValue);
    const closeModal = () => {
        setModalData({
            modalType: '',
            data: false,
            showAddRow: modalData.showAddRow,
            value: '',
            category: ''
        });
    };

    useEffect(() => {
        if (Object.keys(modalData).length > 0 && modalData.data) {
           setRowData(modalData.data);
        } 
    }, [modalData]);

    useEffect(() => {
        if (stateList.length) {
            setFinalValue(prevState => ({
                ...prevState,
                stateList,
                trafficSourceIdList: _.has(rowData, 'trafficSourceIdList') ? rowData.trafficSourceIdList : prevState.trafficSourceIdList
            }));
            setStateList('');
        }
    }, [stateList]);

    useEffect(() => {
        if (trafficSourceIdList.length) {
            setFinalValue(prevState => ({
                ...prevState,
                trafficSourceIdList,
                stateList:  _.has(rowData, 'stateList') ? rowData.stateList : prevState.stateList
            }));
            setTrafficSourceIdList('');
        }
    }, [trafficSourceIdList]);
    /**
     * TrafficSourceStateGroup Data GraphQL
     */
    const {loading, error, data, refetch} = useQuery(gql`
        query allTrafficSourceStateGroups {
            allTrafficSourceStateGroup {
                nodes {
                    id
                    dateCreated
                    dateModified
                    blockReason
                    name
                    trafficSourceIdList
                    stateList
                }
            }
            allBlockReasons: __type(name: "BlockReason"){
                enumValues {
                    name
                }
            }
            
            allTrafficSource(filter: {
                includeDeleted: true
            }) {
                nodes {
                    id
                    name
                    legacyPromoId
                    minimumPrice
                    affiliate {
                        id
                        name
                    }
                    isDeleted
                }
            }
        }
    `, {
        notifyOnNetworkStatusChange: true,
    });

    /**
     * Create Single Value TrafficSourceStateGroup Graphql
     */
    const [createTrafficSourceStateGroup, {loading: createTrafficSourceStateGroupLoading, error: createTrafficSourceStateGroupError}] = useMutation(gql`
        mutation CreateTrafficSourceStateGroup(
            $stateList: String!
            $trafficSourceIdList: String!
            $expires: DateTime
            $blockReason: BlockReason!
            $name: String!
        ) {
            createTrafficSourceStateGroup(
                expires: $expires
                stateList: $stateList
                trafficSourceIdList: $trafficSourceIdList
                blockReason: $blockReason
                name: $name
            ) {
                id
                dateCreated
                dateModified
                blockReason
                name
                trafficSourceIdList
                stateList
            }
        }
    `);
    /**
     * Update TrafficSourceStateGroup Graphql
     */
    const [updateTrafficSourceStateGroup, {loading: updateTrafficSourceStateGroupLoading, error: updateTrafficSourceStateGroupError}] = useMutation(gql`
        mutation UpdateTrafficSourceStateGroup(
            $id: UUID!
            $stateList: String!
            $trafficSourceIdList: String!
            $name: String!
            $blockReason: BlockReason!
        ) {
            updateTrafficSourceStateGroup(
                id: $id
                stateList: $stateList
                trafficSourceIdList: $trafficSourceIdList
                name: $name
                blockReason: $blockReason
            ) {
                id
                dateCreated
                dateModified
                blockReason
                name
                trafficSourceIdList
                stateList
            }
        }
    `);
    /**
     * Delete TrafficSourceStateGroup GraphQL
     */
    const [deleteTrafficSourceStateGroup, {loading: deleteTrafficSourceStateGroupLoading}] = useMutation(gql`
        mutation DeleteTrafficSourceStateGroup(
            $id: UUID!
        ) {
            deleteTrafficSourceStateGroup(
                id: $id
            )
        }
    `);

    useEffect(() => {
        if (!deleteTrafficSourceStateGroupLoading) {
            setFinalValue(resetFinalValue);
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteTrafficSourceStateGroupLoading]);

    useEffect(() => {
        if (!createTrafficSourceStateGroupLoading) {
            closeModal();
            setFinalValue(resetFinalValue);
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createTrafficSourceStateGroupLoading]);

    useEffect(() => {
        if (!updateTrafficSourceStateGroupLoading) {
            closeModal();
            setFinalValue(resetFinalValue);
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateTrafficSourceStateGroupLoading]);

    /**
     * Main Return for Component
     */
    return (
        <>
            <Typography variant="h2">
                Traffic Source State Groups
            </Typography>
            {!loading && (error || updateTrafficSourceStateGroupError || createTrafficSourceStateGroupError) ? (
                <Paper className={classes.paper}>
                    <Alert severity="error">There is an error!</Alert>
                    <pre>{JSON.stringify(error || updateTrafficSourceStateGroupError || createTrafficSourceStateGroupError, null, 2)}</pre>
                </Paper>
            ) : null}
            {loading ? (
                <Paper className={classes.paper}>
                    <div className={classes.loadingBar}>
                        <CircularProgress
                            size={50}
                            thickness={4}
                        />
                    </div>
                </Paper>
            ) : null}
            {!loading && data && data.allTrafficSourceStateGroup.nodes ? (
                <TrafficSourceStateGroupTable
                    refetch={refetch}
                    data={data}
                    setSuccess={setSuccess}
                    updateTrafficSourceStateGroupLoading={updateTrafficSourceStateGroupLoading}
                    createTrafficSourceStateGroup={createTrafficSourceStateGroup}
                    deleteTrafficSourceStateGroup={deleteTrafficSourceStateGroup}
                    updateTrafficSourceStateGroup={updateTrafficSourceStateGroup}
                    setModalData={setModalData}
                    finalValue={finalValue}
                    setFinalValue={setFinalValue}
                    setTrafficSourceIdList={setTrafficSourceIdList}
                    setStateList={setStateList}
                />
            ) : null}
            {modalData.modalType === 'stateList' ? (
                <StateModal
                    data={data}
                    modalData={modalData}
                    closeModal={closeModal}
                    setModalData={setModalData}
                    setStateList={setStateList}
                />
            ) : null}
            {modalData.modalType === 'trafficSourceIdList' ? (
                <TrafficSourceIdListModal
                    data={data}
                    modalData={modalData}
                    closeModal={closeModal}
                    setTrafficSourceIdList={setTrafficSourceIdList}
                />
            ) : null}
        </>
    );
};

export default TrafficSourceStateGroups;
